import React from 'react';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import { Box, Stack, Typography } from '@mui/material';

import { AppointmentTypesSetupSignals } from './AppointmentTypesSetupSignals';
import { PracticeSectionCard } from './PracticeSectionCard';
import { SectionEntryRow } from './SectionEntryRow';

export interface ScheduleAndDocumentationViewProps {
  /**
   * Show the Scheduling sub-section — gated like its standalone tab was:
   * `manage_org_calendar` + the PATIENT_SELF_SCHEDULING feature gate
   * (default-off until rollout, GDN-2187).
   */
  showScheduling: boolean;
  /** Show the Note Templates sub-section (`manage_templates`). */
  showNoteTemplates: boolean;
  /** Show the Appointment Types sub-section (`manage_appointment_types`). */
  showAppointmentTypes: boolean;
  /** Show the Appointment Labels sub-section (`manage_appointment_labels`). */
  showAppointmentLabels: boolean;
  /**
   * Show the appointment-type catalog entry — gated by
   * `feature_practice_type_catalog`, separate from the section's own
   * `manage_appointment_types` gate.
   */
  showAppointmentTypeCatalog: boolean;
  /** Appointment types available to the practice — drives the setup overview. */
  appointmentTypeCount: number;
  /**
   * The appointment-type cleanup-wizard entry, injected by the host (the real
   * gated `CleanupWizardEntry variant="row"`, or a presentational row in
   * Storybook). Rendered as the last row of the Appointment Types section when
   * present.
   */
  appointmentTypeCleanupWizard?: React.ReactNode;
  /**
   * The appointment-labels manager, injected by the host (the real
   * `AppointmentLabelsManager`, or a stand-in in Storybook). Embedded inside
   * the Appointment Labels card — labels are managed right here rather than on
   * a separate page.
   */
  appointmentLabelsManager?: React.ReactNode;

  /** Open the scheduling settings (availability + booking gates). */
  onManageScheduling: () => void;
  /** Open the note-template management workbench (`/templates`). */
  onManageNoteTemplates: () => void;
  /** Open the note-template catalog. */
  onOpenNoteTemplateCatalog: () => void;
  /** Open the appointment-type management modal. */
  onManageAppointmentTypes: () => void;
  /** Open the appointment-type catalog. */
  onOpenAppointmentTypeCatalog: () => void;
}

/**
 * Presentational "Schedule & Documentation" practice section — one bordered
 * card per sub-section (Scheduling, Note Templates, Appointment Types,
 * Appointment Labels), each with an educational header + a primary "Manage …"
 * action and, where it applies, divider-separated browse entries (catalog,
 * cleanup wizard). Each sub-section renders only when its `show*` flag is
 * true; the wired container derives those flags from feature gates +
 * `useCan(...)` and supplies the navigation / modal handlers. No hooks here,
 * so it drives cleanly from Storybook.
 */
export const ScheduleAndDocumentationView: React.FC<
  ScheduleAndDocumentationViewProps
> = ({
  showScheduling,
  showNoteTemplates,
  showAppointmentTypes,
  showAppointmentLabels,
  showAppointmentTypeCatalog,
  appointmentTypeCount,
  appointmentTypeCleanupWizard,
  appointmentLabelsManager,
  onManageScheduling,
  onManageNoteTemplates,
  onOpenNoteTemplateCatalog,
  onManageAppointmentTypes,
  onOpenAppointmentTypeCatalog,
}) => {
  const hasAnySection =
    showScheduling ||
    showNoteTemplates ||
    showAppointmentTypes ||
    showAppointmentLabels;

  return (
    <Stack spacing={3} sx={{ width: '100%' }}>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {'Schedule & Documentation'}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
          Manage the scheduling setup, note templates, appointment types, and
          labels your practice uses to schedule and chart.
        </Typography>
      </Box>

      {!hasAnySection && (
        <Typography variant="body2" color="text.secondary">
          {"You don't have access to any schedule or documentation settings."}
        </Typography>
      )}

      {showScheduling && (
        <PracticeSectionCard
          heading="Scheduling"
          description="Configure self-serve booking — your availability and the gates that screen who can land on your calendar."
          manageLabel="Manage scheduling"
          onManage={onManageScheduling}
        />
      )}

      {showNoteTemplates && (
        <PracticeSectionCard
          heading="Note Templates"
          description="Control the note templates your team charts with. Each template sets the sections, structure, and prompts a note starts from — keep them organized across practice-wide, per-clinician, and adopted public templates."
          manageLabel="Manage templates"
          onManage={onManageNoteTemplates}
        >
          <SectionEntryRow
            icon={<TableChartOutlinedIcon />}
            title="Browse the full catalog"
            description="See every note template across your practice — practice-wide, each clinician's, and public templates they've adopted — with usage and the appointment types that use them, and archive or remove anything."
            actionLabel="Open catalog"
            onAction={onOpenNoteTemplateCatalog}
          />
        </PracticeSectionCard>
      )}

      {showAppointmentTypes && (
        <PracticeSectionCard
          heading="Appointment Types"
          description="Control the visit options your team chooses from while scheduling and preparing encounters. These defaults help each appointment start with the right duration, color, note template, forms, and reminders."
          manageLabel="Manage appointment types"
          onManage={onManageAppointmentTypes}
          overview={<AppointmentTypesSetupSignals count={appointmentTypeCount} />}
        >
          {showAppointmentTypeCatalog && (
            <SectionEntryRow
              icon={<TableChartOutlinedIcon />}
              title="Browse the full catalog"
              description="See every appointment type across your practice — who owns it, how much it's used, and the note template behind it — and archive or restore anything."
              actionLabel="Open catalog"
              onAction={onOpenAppointmentTypeCatalog}
            />
          )}
          {appointmentTypeCleanupWizard}
        </PracticeSectionCard>
      )}

      {showAppointmentLabels && (
        <PracticeSectionCard
          heading="Appointment Labels"
          description="Create labels and groups to organize your calendar into columns. For example, a “Spravato Rooms” group with “Room 1” and “Room 2,” or a “Department” group with “Psychiatry” and “Psychology.”"
        >
          {appointmentLabelsManager}
        </PracticeSectionCard>
      )}
    </Stack>
  );
};
